import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { searchNationalities } from "@/lib/nationalities";

/**
 * Nationality autocomplete — text input that opens a dropdown of matching
 * countries / nationalities once the user has typed 3+ characters. Matches
 * against the demonym ("Indian"), full country name ("India") and ISO-alpha-2
 * code ("IN"). Selecting an entry writes the demonym back to `value`.
 *
 * Props:
 *   value, onChange, error, testId, placeholder
 */
export default function NationalityAutocomplete({
  value,
  onChange,
  error,
  testId,
  placeholder = "Start typing…",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const opts = searchNationalities(value);

  // Close the panel when the user clicks anywhere outside the wrapper
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (n) => {
    onChange(n.name);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || !opts.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((i) => Math.min(i + 1, opts.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter")   { e.preventDefault(); pick(opts[hi]); }
    else if (e.key === "Escape")  { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        data-testid={testId}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        autoComplete="off"
        className={`h-11 ${error ? "border-red-400 focus-visible:ring-red-300" : ""} ${className}`}
      />
      {open && opts.length > 0 && (
        <div
          data-testid={`${testId}-dropdown`}
          className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-72 overflow-y-auto py-1"
        >
          {opts.map((n, i) => (
            <button
              key={n.code}
              type="button"
              data-testid={`${testId}-option-${n.code}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(n)}
              onMouseEnter={() => setHi(i)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${
                hi === i ? "bg-teal-50" : "hover:bg-slate-50"
              }`}
            >
              <span>
                <span className="font-medium text-[var(--brand-accent)]">{n.name}</span>
                <span className="text-slate-500"> · {n.country}</span>
              </span>
              <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{n.code}</span>
            </button>
          ))}
        </div>
      )}
      {open && (value || "").trim().length >= 1 && opts.length === 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-md px-3 py-2 text-xs text-slate-500">
          No matches — keep typing or use a different spelling.
        </div>
      )}
    </div>
  );
}
